import React, { useContext, useState, useEffect, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { orderBy, filterBy } from '@progress/kendo-data-query';
import Loader from '../../components/Loader';
import BannerEditor from './BannerEditor';
import { useBanners } from '../../hooks';
import { LocalizationContext } from '../../contexts';
import { stringScoreNormalizer } from '../../utils/helper';
import { languageWorkspaceOptions } from '../../config';
import './index.scss';

export default function BannersManagement({ match }) {
    const history = useHistory();
    const [localization] = useContext(LocalizationContext);
    const { bannerID } = match.params;

    const {
        lang,
        setLang,
        userType,
        setUserType,

        loadingDimensions,
        loadingDimensionsError,
        disciplines,
        ageGrades,
        sectors,

        bannersList,
        loadingBannersList,
        bannersListLoadingError,

        setEditItemId,
        isEditItemLoading,
        errorEditItemLoading,
        editItem,

        saveItem,
        isItemSaving,
        saveItemError,

        deleteItem,
        isItemDeleting,
        itemDeletingError,

        clearApisErrors
    } = useBanners();

    const [gridPaging, setGridPaging] = useState({ skip: 0, take: 20 });
    const [gridSort, setGridSort] = useState({ allowUnsort: true, multiple: false, sort: [{ field: "lastUpdatedDate", dir: "desc" }] });
    const [gridFilter, setGridFilter] = useState({ logic: "and", filters: [] });
    const [gridItems, setGridItems] = useState([]);

    useLayoutEffect(() => {
        updateGridItems(bannersList, gridSort.sort, gridFilter);
    }, [bannersList]);

    useLayoutEffect(() => {
        setEditItemId(bannerID ? (bannerID === 'new' ? bannerID : Number(bannerID)) : 0);
    }, [bannerID]);

    const updateGridItems = (items, sort, filter) => {
        var sortItems = orderBy(items, sort);
        var filters = filter;
        if (filters && filters.filters && filters.filters.length > 0) {
            filters = { ...filter };
            filters.filters.filter(f => f.field === 'title').map(f => {
                f.value = f.value ? stringScoreNormalizer(f.value) : '';
            })
        }
        var filterItems = filterBy(sortItems, filters);
        setGridItems(filterItems);
    };

    const sortChange = (event) => {
        setGridSort({ ...gridSort, sort: event.sort });
        updateGridItems([...bannersList], event.sort, gridFilter);
    };

    const columns = [
        { key: 'id', type: 'text', props: { width: '150px' } },
        {
            key: 'title', type: 'text', props: {
                cell: (props) => (
                    <td role="gridcell" className="app-banners_management-grid-column--title" onClick={() => { history.push('/internalPages/' + props.dataItem.id); }}>
                        {props.dataItem[props.field] ? props.dataItem[props.field].split('<!--###-->')[0] : ''}
                    </td>
                )
            }
        },
        { 
            key: 'type', 
            type: 'text', 
            select: Object.keys(localization.labels.bannersManagement.searchResult.types).map(key => localization.labels.bannersManagement.searchResult.types[key]),
            props: { 
                width: '210px'
            }
        },
        { key: 'lastUpdatedDate', type: 'date', props: { format: "{0:dd/MM/y HH:mm}", className: "gridcell-ltr", width: '180px' } },
        { key: 'isPublish', type: 'boolean', props: { width: '120px' } },
    ].map((column, i) => {
        var props = column.props || {};
        props.field = column.key;
        props.filter = column.type;
        props.title = column.key in localization.labels.bannersManagement.searchResult ? localization.labels.bannersManagement.searchResult[column.key] : column.key;
        var cell = false;
        var columnMenu = false;
        var filterCell = false;
        switch (column.type) {
            case 'text':
                if (column.select) {
                    filterCell = (props) => {
                        return <div className="k-filtercell">
                            <DropDownList
                                data={column.select}
                                value={props.value}
                                onChange={event => {
                                    const hasValue = (event.target.value && event.target.value != '');
                                    props.onChange({
                                        value: hasValue ? event.target.value : '',
                                        operator: hasValue ? 'contains' : '',//'eq'
                                        syntheticEvent: event.syntheticEvent
                                    });
                                }}
                            />
                            {(props.value && props.value !== '') && <button
                                className="k-button k-button-icon k-clear-button-visible"
                                onClick={event => {
                                    event.preventDefault();
                                    props.onChange({
                                        value: '',
                                        operator: '',
                                        syntheticEvent: event
                                    });
                                }}
                            ><span className="k-icon k-i-filter-clear" /></button>}
                        </div>
                    }
                }
                break;
            case 'numeric':
                break;
            case 'boolean':
                cell = props => (
                    <td role="gridcell">
                        <Checkbox disabled={false} style={{ pointerEvents: 'none' }} value={props.dataItem[props.field]} />
                    </td>
                );
                break;
            case 'date':
                break;
            default:
                break;
        }
        if (cell) props.cell = cell;
        if (columnMenu) props.columnMenu = columnMenu;
        if (filterCell) props.filterCell = filterCell;
        return <Column {...props} dir="ltr" key={'Column_' + i} />;
    });

    var rowNum = 0;
    const getRowNumber = () => {
        rowNum++;
        return rowNum;
    };

    // Build languages list from languageWorkspaceOptions
    const languages = languageWorkspaceOptions.map(option => ({
        id: option.id,
        nameHe: localization.labels[option.labelKey]
    }));
    const current_language = languages.find(d => d.id === lang);

    const userTypes = [
        {id: "teacher", text: localization.labels.bannersManagement.userTypes.teacher},
        {id: "coordinator", text: localization.labels.bannersManagement.userTypes.coordinator, itemDisabled: true},
        {id: "director", text: localization.labels.bannersManagement.userTypes.director, itemDisabled: true},
    ]
    const current_userType = userTypes.find(d => d.id === userType);

    return <div className="app-banners_management">
        {(loadingDimensions || loadingBannersList || isEditItemLoading || isItemDeleting) && <Loader />}
        {(loadingDimensionsError || bannersListLoadingError || errorEditItemLoading) && <Dialog onClose={() => {clearApisErrors()}} title={localization.labels.loading_error_title}><p>{localization.labels.loading_error_message}</p></Dialog>}
        {itemDeletingError && <Dialog onClose={() => { clearApisErrors(); }} title={localization.labels.delete_error_title}><p>{localization.labels.delete_error_message}</p></Dialog>}
        <div className="app-banners_management-grid">
            <Grid
                className="app-grid"
                data={gridItems.slice(gridPaging.skip, gridPaging.take + gridPaging.skip)}
                skip={gridPaging.skip}
                take={gridPaging.take}
                total={gridItems.length}
                pageable={{
                    buttonCount: 3,
                    info: true,
                    type: 'numeric',
                    pageSizes: [5, 10, 15, 20, 25, 30, 40, 50, 100],
                    previousNext: true,
                    messages: localization.labels.grid_messages
                }}
                onPageChange={(event) => { setGridPaging({ skip: event.page.skip, take: event.page.take }); }}
                sortable={{
                allowUnsort: gridSort.allowUnsort,
                mode: gridSort.multiple ? 'multiple' : 'single'
                }}
                sort={gridSort.sort}
                onSortChange={sortChange}
                filterable
                filter={gridFilter}
                filterOperators={{
                    date: [
                        { text: 'grid.filterAfterOperator', operator: 'gt' },
                        { text: 'grid.filterBeforeOperator', operator: 'lt' },
                    ],
                    'text': [
                        { text: 'grid.filterContainsOperator', operator: 'contains' },
                        { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
                        { text: 'grid.filterEqOperator', operator: 'eq' },
                        { text: 'grid.filterNotEqOperator', operator: 'neq' },
                        { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
                        { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
                        { text: 'grid.filterIsNullOperator', operator: 'isnull' },
                        { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
                        { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
                        { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
                    ],
                }}
                onFilterChange={(e) => {
                    setGridPaging({ ...gridPaging, skip: 0 });
                    setGridFilter(e.filter);
                    updateGridItems([...bannersList], gridSort.sort, e.filter);
                }}
            >
                <GridToolbar>
                    <>
                        <div className="app-banners_management-grid-title">{localization.labels.bannersManagement.grid_title}</div>
                        <div className="app-banners_management-grid-tools">
                            <div className="app-banners_management-grid-tools--left">
                                <DropDownList
                                    data={userTypes}
                                    value={current_userType}
                                    textField="text"
                                    dataItemKey="id"
                                    label={localization.labels.bannersManagement.userType_label}
                                    onChange={(event) => {
                                        setUserType(event.target.value.id);
                                    }}
                                    itemRender={(element, { dataItem }) => {
                                        if (!dataItem || !dataItem.itemDisabled) {
                                            return element;
                                        }

                                        const children = [
                                            <span
                                              key={dataItem.id}
                                              style={{
                                                color: '#ccc',
                                              }}
                                            >
                                              {dataItem.text}
                                            </span>,
                                        ];

                                        return React.cloneElement(
                                            element,
                                            {
                                              ...element.props,
                                              style: {
                                                ...element.props.style,
                                                cursor: 'default',
                                              },
                                              onClick: () => {},
                                            },
                                            children,
                                        );
                                    }}
                                />
                                <DropDownList
                                    data={languages}
                                    value={current_language}
                                    textField={localization.labels.bannersManagement.languages_textField}
                                    dataItemKey="id"
                                    label={localization.labels.bannersManagement.languages_label}
                                    onChange={(event) => {
                                        setLang(event.target.value.id);
                                    }}
                                />
                            </div>
                            <div className="app-banners_management-grid-tools--right">
                                <Button className="new_item k-button" primary={true} onClick={() => {
                                    history.push('/internalPages/new');
                                }}>{localization.labels.bannersManagement.new_item}</Button>
                            </div>
                        </div>
                    </>
                </GridToolbar>
                <Column
                    sortable={false}
                filterable={false}
                width="48px"
                locked={true}
                cell={props => (
                    <td role="gridcell" style={props.style} className="grid-row_num k-grid-content-sticky">
                        {getRowNumber() + gridPaging.skip}
                    </td>
                    )}
                />
                {columns}
            </Grid>
        </div>
        {editItem && ageGrades && disciplines && sectors && ageGrades.length && disciplines.length && sectors.length && <BannerEditor 
            data={editItem} 
            ageGradesList={ageGrades}
            disciplinesList={disciplines}
            sectorsList={sectors}
            update={saveItem} 
            deleteBanner={deleteItem} 
        />}
    </div>
};